/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import JSG from '@cedalo/jsg-ui';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import SvgIcon from '@mui/material/SvgIcon/SvgIcon';
import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import Menu from 'material-ui-popup-state/HoverMenu';
import TimelineIcon from '@mui/icons-material/esm/Timeline';
import ShortTextIcon from '@mui/icons-material/esm/ShortText';
import ChevronRight from '@mui/icons-material/esm/ChevronRight';
import { graphManager } from '../../../GraphManager';
import { IconCopy, IconCut } from '../../../components/icons';
import DeleteIcon from '@mui/icons-material/esm/Delete';
import SettingsIcon from '@mui/icons-material/esm/Settings';
import PopupState, { bindHover, bindMenu } from 'material-ui-popup-state';

const ParentPopupState = React.createContext(null);

const styles = {
	menuItem: {
		transition: 'none'
	}
};

const Submenu =
	// Unfortunately, MUI <Menu> injects refs into its children, which causes a
	// warning in some cases unless we use forwardRef here.
	React.forwardRef(({ classes, title, popupId, children, ...props }, ref) => (
		<ParentPopupState.Consumer>
			{parentPopupState => (
				<PopupState
					variant='popover'
					popupId={popupId}
					parentPopupState={parentPopupState}
				>
					{popupState => (
						<ParentPopupState.Provider value={popupState}>
							<MenuItem
								dense
								{...bindHover(popupState)}
								selected={popupState.isOpen}
								ref={ref}
							>
								<ListItemIcon key="iconSGM">
									<SvgIcon style={styles.menuItem} />
								</ListItemIcon>
								<ListItemText
									key={title}
									primary={title}
								/>
								<ChevronRight
									sx={{
										marginRight: (theme) => theme.spacing(-1)
									}}
								/>
							</MenuItem>
							<Menu
								{...bindMenu(popupState)}
								sx={{
									'&.MuiMenu-paper': {
										marginTop: (theme) => theme.spacing(-1)
									}
								}}
								anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
								transformOrigin={{ vertical: 'top', horizontal: 'left' }}
								getContentAnchorEl={null}
								{...props}
							>
								{children}
							</Menu>
						</ParentPopupState.Provider>)}
				</PopupState>)}
		</ParentPopupState.Consumer>));

export default class GraphContextMenu extends Component {
	constructor(props) {
		super(props);
		this.state = {
			context: false, top: '0px', left: '0px'
		};
	}

	componentDidMount() {
		document.addEventListener('click', this.handleClick);
		JSG.NotificationCenter.getInstance().register(this, JSG.GRAPH_SHOW_CONTEXT_MENU_NOTIFICATION, 'onContextMenu');

		/* eslint-disable react/no-did-mount-set-state */
		this.setState({
			context: false
		});

		/* eslint-enable react/no-did-mount-set-state */
	}

	componentWillUnmount() {
		document.removeEventListener('click', this.handleClick);
		JSG.NotificationCenter.getInstance().unregister(this, JSG.GRAPH_SHOW_CONTEXT_MENU_NOTIFICATION);
	}

	onContextMenu(data) {
		let left = data.object.event.event.offsetX;
		const maxRight = left + 235;
		if (maxRight > window.innerWidth) {
			left = window.innerWidth - 235;
		}
		const popHeight = 500;
		const maxTop = window.innerHeight - popHeight > 0 ? window.innerHeight - popHeight : 0;
		const top = data.object.event.event.offsetY > maxTop ? maxTop : data.object.event.event.offsetY;

		this.setState({
			context: true, left, top
		});
	}

	onCopy = () => {
		graphManager
			.getGraphEditor()
			.getInteractionHandler()
			.copySelection();
	};

	onCut = () => {
		graphManager
			.getGraphEditor()
			.getInteractionHandler()
			.cutSelection();
	};

	onDelete = () => {
		graphManager
			.getGraphEditor()
			.getInteractionHandler()
			.deleteSelection();
	};

	onChangeOrder = (popupState, order) => {
		popupState.close();

		const viewer = graphManager.getGraphViewer();
		if (viewer === undefined) {
			return;
		}

		const selection = viewer.getSelection();
		const item = selection.length ? selection[0].getModel() : undefined;

		graphManager.synchronizedExecute(new JSG.ChangeItemOrderCommand(item, order, viewer));
	};

	onAlignShapes = (popupState, alignment) => {
		popupState.close();

		graphManager
			.getGraphEditor()
			.getInteractionHandler()
			.alignSelection(alignment);
	};

	onEditPoints = () => {
		const viewer = graphManager.getGraphViewer();
		if (viewer === undefined) {
			return;
		}

		viewer.getInteractionHandler().editSelection();
	};

	onAddText = () => {
		const viewer = graphManager.getGraphViewer();
		if (viewer === undefined) {
			return;
		}

		const selection = viewer.getSelection();
		if (selection.length !== 1) {
			return;
		}

		const cs = viewer.getCoordinateSystem();

		const position = new JSG.Point(this.state.left, this.state.top);
		const relPos = cs.deviceToLogPoint(position);

		viewer.translateFromParent(relPos);

		JSG.GraphUtils.traverseDown(viewer.getGraphView(), selection[0].getView(), (v) => {
			v.translateFromParent(relPos);
			return true;
		});

		const item = selection[0].getModel();
		if (item.isAddLabelAllowed()) {
			const node = new JSG.TextNode('Text');
			const pos = item.getNewLabelPosition();
			if (pos !== undefined) {
				node.setCenter(relPos.x, relPos.y);
				node.getTextFormat().setVerticalPosition(0);
				node.getTextFormat().setHorizontalPosition(0);
			}
			node.getTextFormat().setParent(item.getTextFormat());
			node.setItemAttribute(JSG.ItemAttributes.SNAPTO, false);

			graphManager.synchronizedExecute(new JSG.AddItemCommand(node, item));
		}
	};

	canAddText() {
		const viewer = graphManager.getGraphViewer();
		if (viewer === undefined) {
			return false;
		}

		const selection = viewer.getSelection();
		if (selection.length !== 1) {
			return false;
		}

		return selection[0].getModel().isAddLabelAllowed();
	}

	isMultiSelection() {
		const viewer = graphManager.getGraphViewer();
		if (viewer === undefined) {
			return false;
		}

		const selection = viewer.getSelection();
		return selection.length > 1;
	}

	canEditPoints() {
		const viewer = graphManager.getGraphViewer();
		if (viewer === undefined) {
			return false;
		}

		const selection = viewer.getSelection();
		if (selection.length !== 1) {
			return false;
		}

		const shapeType = selection[0]
			.getModel()
			.getShape()
			.getType();

		switch (shapeType) {
		case JSG.PolygonShape.TYPE:
		case JSG.BezierShape.TYPE:
			return true;
		default:
			return false;
		}
	}

	onShowShapeSettings = () => {
		JSG.NotificationCenter.getInstance().send(
			new JSG.Notification(JSG.SHOW_SHAPE_SETTINGS, {
				open: true
			})
		);
	};

	handleClick = (event) => {
		const { context } = this.state;
		const wasOutside = !(event.target.contains === this.sheetmenu);

		if (wasOutside && context) {
			this.setState({
				context: false
			});
		}
	};

	render() {
		if (!this.state.context || graphManager === undefined || graphManager.getGraphViewer() === undefined) {
			return null;
		}
		const selection = graphManager.getGraphViewer().getSelection();
		const item = selection.length ? selection[0].getModel() : undefined;
		const showEdit = this.canEditPoints();
		const showAddText = this.canAddText();
		const dashBoard = item && item.getItemType() === 'layoutnode';

		return (<Paper
				id='sheetmenu'
				ref={(ref) => {
					this.sheetmenu = ref;
				}}
				style={{
					display: 'inline-block',
					position: 'absolute',
					left: `${this.state.left}px`,
					top: `${this.state.top}px`,
				}}
			>

				<PopupState variant='popover' popupId='demoMenu'>
					{popupState => (<ParentPopupState.Provider value={popupState}>
							<MenuList
								{...bindHover(popupState)}
								{...bindMenu(popupState)}
							>
								{!this.isMultiSelection() ? [
										<MenuItem key="2" onClick={this.onShowShapeSettings} dense>
											{item instanceof JSG.SheetPlotNode ? [<ListItemIcon key="iconGCM">
												<SvgIcon style={styles.menuItem}>
													<path d='M22,21H2V3H4V19H6V10H10V19H12V6H16V19H18V14H22V21Z' />
												</SvgIcon>
											</ListItemIcon>, <ListItemText key="textGCM"
												primary={<FormattedMessage id='ContextMenus.EditChart' defaultMessage='Edit Chart' />}
											/>] : [<ListItemIcon key="iconGCM">
												<SettingsIcon style={styles.menuItem} />
											</ListItemIcon>, <ListItemText key="textGCM"
												primary={<FormattedMessage id='ContextMenus.EditGraphItem' defaultMessage='Edit Object' />}
											/>]}
										</MenuItem>,
									] : null}
								{!this.isMultiSelection() && !dashBoard ?
									<Divider key="31"/> : null
								}
								{
									dashBoard ? null : [
								<MenuItem key="3" onClick={this.onCut} dense>
									<ListItemIcon key="iconGCM">
										<IconCut style={styles.menuItem} />
									</ListItemIcon>
									<ListItemText  key="textGCM" primary={<FormattedMessage id='ContextMenus.Cut' defaultMessage='Cut' />} />
								</MenuItem>,
								<MenuItem key="4" onClick={this.onCopy} dense>
									<ListItemIcon key="iconGCM">
										<IconCopy style={styles.menuItem} />
									</ListItemIcon>
									<ListItemText key="textGCM" primary={<FormattedMessage id='ContextMenus.Copy' defaultMessage='Copy' />} />
								</MenuItem>,
								<MenuItem key="24" onClick={this.onDelete} dense>
									<ListItemIcon key="iconGCM">
										<DeleteIcon style={styles.menuItem} />
									</ListItemIcon>
									<ListItemText key="textGCM" primary={<FormattedMessage id='ContextMenus.Delete' defaultMessage='Delete' />} />
								</MenuItem>,
								<Divider key="35"/>,
								<Submenu
									popupId='drawingOrderMenu' title={<FormattedMessage id='ContextMenus.ChangeOrder'
																					   defaultMessage='Drawing Order' />}
								>
									<MenuItem key="5"
											  onClick={() => this.onChangeOrder(popupState, JSG.ChangeItemOrderCommand.Action.TOTOP)}
										dense>
										<ListItemIcon key="iconGCM">
											<SvgIcon style={styles.menuItem}>
												<path
													d='M2,2H11V6H9V4H4V9H6V11H2V2M22,13V22H13V18H15V20H20V15H18V13H22M8,8H16V16H8V8Z' />
											</SvgIcon>
										</ListItemIcon>
										<ListItemText key="textGCM" primary={<FormattedMessage id='ContextMenus.MoveToTop'
																				 defaultMessage='Move to Top' />} />
									</MenuItem>
									<MenuItem key="6" onClick={() => this.onChangeOrder(popupState, JSG.ChangeItemOrderCommand.Action.UP)}
											  dense>
										<ListItemIcon key="iconGCM">
											<SvgIcon style={styles.menuItem}>
												<path d='M2,2H16V16H2V2M22,8V22H8V18H10V20H20V10H18V8H22Z' />
											</SvgIcon>
										</ListItemIcon>
										<ListItemText key="textGCM"
											primary={<FormattedMessage id='ContextMenus.MoveUp' defaultMessage='Move up' />} />
									</MenuItem>
									<MenuItem key="7" onClick={() => this.onChangeOrder(popupState, JSG.ChangeItemOrderCommand.Action.DOWN)}
											  dense>
										<ListItemIcon key="iconGCM">
											<SvgIcon style={styles.menuItem}>
												<path d='M2,2H16V16H2V2M22,8V22H8V18H18V8H22M4,4V14H14V4H4Z' />
											</SvgIcon>
										</ListItemIcon>
										<ListItemText key="textGCM"
											primary={<FormattedMessage id='ContextMenus.MoveDown' defaultMessage='Move down' />} />
									</MenuItem>
									<MenuItem key="8"
											  onClick={() => this.onChangeOrder(popupState, JSG.ChangeItemOrderCommand.Action.TOBOTTOM)}
										dense>
										<ListItemIcon key="iconGCM">
											<SvgIcon style={styles.menuItem}>
												<path
													d='M2,2H11V11H2V2M9,4H4V9H9V4M22,13V22H13V13H22M15,20H20V15H15V20M16,8V11H13V8H16M11,16H8V13H11V16Z' />
											</SvgIcon>
										</ListItemIcon>
										<ListItemText key="textGCM"
											primary={<FormattedMessage id='ContextMenus.MoveToBottom'
																	   defaultMessage='Move to Bottom' />}
										/>
									</MenuItem>
								</Submenu>
								]}
								{this.isMultiSelection() ? (
									<Submenu
										popupId='AlignMenu' title={<FormattedMessage id='ContextMenus.Align'
																						   defaultMessage='Align Shapes' />}
									>
										<MenuItem key="9"
												  onClick={() => this.onAlignShapes(popupState, JSG.AlignItemsCommand.Alignment.LEFT)}
											dense>
											<ListItemIcon key="iconGCM">
												<SvgIcon style={styles.menuItem}>
													<path
														d="M22 13V19H6V13H22M6 5V11H16V5H6M2 2V22H4V2H2" />
												</SvgIcon>
											</ListItemIcon>
											<ListItemText key="textGCM" primary={<FormattedMessage id='ContextMenus.AlignLeft'
																					 defaultMessage='Align Left' />} />
										</MenuItem>
										<MenuItem key="10"
												  onClick={() => this.onAlignShapes(popupState, JSG.AlignItemsCommand.Alignment.CENTER)}
											dense>
											<ListItemIcon key="iconGCM">
												<SvgIcon style={styles.menuItem}>
													<path
														d="M20 19H13V22H11V19H4V13H11V11H7V5H11V2H13V5H17V11H13V13H20V19Z" />
												</SvgIcon>
											</ListItemIcon>
											<ListItemText key="textGCM" primary={<FormattedMessage id='ContextMenus.AlignCentered'
																					 defaultMessage='Align Centered' />} />
										</MenuItem>
										<MenuItem key="11"
												  onClick={() => this.onAlignShapes(popupState, JSG.AlignItemsCommand.Alignment.RIGHT)}
											dense>
											<ListItemIcon key="iconGCM">
												<SvgIcon style={styles.menuItem}>
													<path
														d="M18 13V19H2V13H18M8 5V11H18V5H8M20 2V22H22V2H20Z" />
												</SvgIcon>
											</ListItemIcon>
											<ListItemText key="textGCM" primary={<FormattedMessage id='ContextMenus.AlignRight'
																					 defaultMessage='Align Right' />} />
										</MenuItem>
										<Divider key="36" />
										<MenuItem key="12"
												  onClick={() => this.onAlignShapes(popupState, JSG.AlignItemsCommand.Alignment.TOP)}
											dense>
											<ListItemIcon key="iconGCM">
												<SvgIcon style={styles.menuItem}>
													<path
														d="M11 22H5V6H11V22M19 6H13V16H19V6M22 2H2V4H22V2Z" />
												</SvgIcon>
											</ListItemIcon>
											<ListItemText key="textGCM" primary={<FormattedMessage id='ContextMenus.AlignTop'
																					 defaultMessage='Align Top' />} />
										</MenuItem>
										<MenuItem key="13"
												  onClick={() => this.onAlignShapes(popupState, JSG.AlignItemsCommand.Alignment.MIDDLE)}
											dense>
											<ListItemIcon key="iconGCM">
												<SvgIcon style={styles.menuItem}>
													<path
														d="M5 20V13H2V11H5V4H11V11H13V7H19V11H22V13H19V17H13V13H11V20H5Z" />
												</SvgIcon>
											</ListItemIcon>
											<ListItemText key="textGCM" primary={<FormattedMessage id='ContextMenus.AlignMiddle'
																					 defaultMessage='Align Middle' />} />
										</MenuItem>
										<MenuItem key="14"
												  onClick={() => this.onAlignShapes(popupState, JSG.AlignItemsCommand.Alignment.BOTTOM)}
											dense>
											<ListItemIcon key="iconGCM">
												<SvgIcon style={styles.menuItem}>
													<path
														d="M11 18H5V2H11V18M19 8H13V18H19V8M22 20H2V22H22V20Z" />
												</SvgIcon>
											</ListItemIcon>
											<ListItemText key="textGCM" primary={<FormattedMessage id='ContextMenus.AlignBottom'
																					 defaultMessage='Align Bottom' />} />
										</MenuItem>
									</Submenu>) : null
								}
								{this.isMultiSelection() ? (<Submenu
									popupId='DistMenu' title={<FormattedMessage id='ContextMenus.Distribute'
																				 defaultMessage='Distribute Shapes' />}
								>
									<MenuItem key="15"
											  onClick={() => this.onAlignShapes(popupState,
											JSG.AlignItemsCommand.Alignment.HDISTRIBUTE)}
										dense>
										<ListItemIcon key="iconGCM">
											<SvgIcon style={styles.menuItem}>
												<path
													d="M9,11H15V8L19,12L15,16V13H9V16L5,12L9,8V11M2,20V4H4V20H2M20,20V4H22V20H20Z" />
											</SvgIcon>
										</ListItemIcon>
										<ListItemText key="textGCM" primary={<FormattedMessage id='ContextMenus.DistributeHorz'
																				 defaultMessage='Horizontal' />} />
									</MenuItem>
									<MenuItem key="16"
											  onClick={() => this.onAlignShapes(popupState,
											JSG.AlignItemsCommand.Alignment.VDISTRIBUTE)}
										dense>
										<ListItemIcon key="iconGCM">
											<SvgIcon style={styles.menuItem}>
												<path
													d="M13,9V15H16L12,19L8,15H11V9H8L12,5L16,9H13M4,2H20V4H4V2M4,20H20V22H4V20Z" />
											</SvgIcon>
										</ListItemIcon>
										<ListItemText key="textGCM" primary={<FormattedMessage id='ContextMenus.DistributeVert'
																				 defaultMessage='Vertical' />} />
									</MenuItem>
								</Submenu>) : null}
								{showEdit && !dashBoard ? <Divider  key="33"/> : null}
								{showEdit ? (<MenuItem key="17" onClick={() => this.onEditPoints()} dense>
										<ListItemIcon key="iconGCM">
											<TimelineIcon style={styles.menuItem} />
										</ListItemIcon>
										<ListItemText key="textGCM" primary={<FormattedMessage id='ContextMenus.EditPoints'
																				 defaultMessage='Edit Points' />} />
									</MenuItem>) : null}
								{showAddText ? <Divider  key="34"/> : null}
								{showAddText ? (<MenuItem key="18" onClick={() => this.onAddText()} dense>
										<ListItemIcon key="iconGCM">
											<ShortTextIcon style={styles.menuItem} />
										</ListItemIcon>
										<ListItemText key="textGCM"
											primary={<FormattedMessage id='ContextMenus.AddText' defaultMessage='Add Text' />} />
									</MenuItem>) : null}
							</MenuList>
						</ParentPopupState.Provider>)}
				</PopupState>
			</Paper>);
	}
}
